import { Assessment, MonetizationOn, RemoveCircleOutline, Schedule, Visibility } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from '../../services/localstorage';
import DashboardService from '../../services/dashboard';
import PayrollService from '../../services/payroll';
import UserService from '../../services/user';
import {
  firstDayOfMonthInput,
  formatARS,
  isDateWithinRange,
  nowDateInput,
  resolveCashierId,
  resolveCashierName,
  round2,
} from './payroll.utils';

const HOUR_MS = 3600000;
const SHIFT_CHANGE_HOUR = 16;
const ARGENTINA_OFFSET_MS = -3 * HOUR_MS;
const SHIFT_KEYS = ['morning', 'afternoon'];

const minDate = (a, b) => (a < b ? a : b);
const maxDate = (a, b) => (a > b ? a : b);

const toDateKey = (date) => {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

const toArgentinaWallDate = (date) => new Date(date.getTime() + ARGENTINA_OFFSET_MS);

const getArgentinaDayBoundaries = (date) => {
  const wallDate = toArgentinaWallDate(date);
  const year = wallDate.getUTCFullYear();
  const month = wallDate.getUTCMonth();
  const day = wallDate.getUTCDate();

  const dayStart = new Date(Date.UTC(year, month, day, 0, 0, 0, 0) - ARGENTINA_OFFSET_MS);
  const shiftChange = new Date(Date.UTC(year, month, day, SHIFT_CHANGE_HOUR, 0, 0, 0) - ARGENTINA_OFFSET_MS);
  const dayEnd = new Date(Date.UTC(year, month, day + 1, 0, 0, 0, 0) - ARGENTINA_OFFSET_MS);

  return {
    dayStart,
    shiftChange,
    dayEnd,
    wallDate,
  };
};

const formatDateLabel = (date) => toArgentinaWallDate(date).toLocaleDateString('es-AR', {
  timeZone: 'UTC',
  day: '2-digit',
  month: '2-digit',
});

const formatHours = (hours) => {
  const safeHours = Number(hours || 0);
  const totalMinutes = Math.round(safeHours * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = Math.abs(totalMinutes % 60);

  if (wholeHours === 0) return `${minutes} min`;
  if (minutes === 0) return `${wholeHours} h`;
  return `${wholeHours} h ${minutes} min`;
};

const formatTime = (date) => {
  if (!date) return '--:--';
  const d = toArgentinaWallDate(new Date(date));
  if (Number.isNaN(d.getTime())) return '--:--';
  const hours = `${d.getUTCHours()}`.padStart(2, '0');
  const minutes = `${d.getUTCMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatSelectedShiftLabel = (selectedShifts) => {
  const hasMorning = selectedShifts.includes('morning');
  const hasAfternoon = selectedShifts.includes('afternoon');
  if (hasMorning && hasAfternoon) return 'Mañana + Tarde';
  if (hasMorning) return 'Solo Mañana';
  if (hasAfternoon) return 'Solo Tarde';
  return 'Sin turnos seleccionados';
};

const getSelectedHoursFromRow = (row, selectedShifts) => {
  const morning = selectedShifts.includes('morning') ? Number(row.morningHours || 0) : 0;
  const afternoon = selectedShifts.includes('afternoon') ? Number(row.afternoonHours || 0) : 0;
  return round2(morning + afternoon);
};

const resolveGroupingId = (entry = {}) => (
  resolveCashierId(entry)
  || entry.cash_register_id
  || entry.cashRegisterId
  || entry._id
  || 'unknown'
);

const resolveGroupingLabel = (entry, usersMap) => {
  const cashierId = resolveCashierId(entry);
  if (cashierId) return resolveCashierName(cashierId, usersMap);

  const cashRegisterId = entry?.cash_register_id || entry?.cashRegisterId || entry?._id;
  if (!cashRegisterId) return 'Sin cajero';

  return `Caja ${String(cashRegisterId).slice(-6)}`;
};

const KpiCard = ({ icon, title, value, subtitle }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          {icon}
          <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Stack>
        <Typography variant="h6" fontWeight={700}>{value}</Typography>
        {subtitle ? <Typography variant="caption" color="text.secondary">{subtitle}</Typography> : null}
      </CardContent>
    </Card>
  </Grid>
);

const asArray = (payload) => (Array.isArray(payload) ? payload : []);

function PayrollSummaryPage({ storeId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [filters, setFilters] = useState({
    from: firstDayOfMonthInput(),
    to: nowDateInput(),
    user_id: '',
  });
  const [hourlyRate, setHourlyRate] = useLocalStorage('payroll-hourly-rate', 0);
  const [selectedShifts, setSelectedShifts] = useLocalStorage('payroll-selected-shifts', SHIFT_KEYS);

  const [historyRows, setHistoryRows] = useState([]);
  const [adjustmentsRows, setAdjustmentsRows] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [shiftView, setShiftView] = useState('daily');
  const [selectedShiftRow, setSelectedShiftRow] = useState(null);

  const normalizedSelectedShifts = useMemo(() => {
    const source = Array.isArray(selectedShifts) ? selectedShifts : SHIFT_KEYS;
    const next = source.filter((value) => SHIFT_KEYS.includes(value));
    return next.length > 0 ? next : SHIFT_KEYS;
  }, [selectedShifts]);

  const usersMap = useMemo(() => {
    const map = new Map();
    asArray(users).forEach((u) => map.set(u._id || u.id, u));
    return map;
  }, [users]);

  const loadData = async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    setWarning(null);
    try {
      const [historyResult, adjustmentsResult, usersResult] = await Promise.allSettled([
        DashboardService.getCashRegisterHistory(storeId, {
          date_from: filters.from,
          date_to: filters.to,
        }),
        PayrollService.listAdjustments(storeId),
        UserService.getAll(),
      ]);

      if (historyResult.status !== 'fulfilled') {
        throw new Error('No se pudo cargar el historial de caja.');
      }

      setHistoryRows(asArray(historyResult.value));

      if (adjustmentsResult.status === 'fulfilled') {
        setAdjustmentsRows(asArray(adjustmentsResult.value));
      } else {
        setAdjustmentsRows([]);
        setWarning('No se pudieron cargar los ajustes. Se muestran solo horas de caja.');
      }

      if (usersResult.status === 'fulfilled') {
        setUsers(asArray(usersResult.value));
      } else {
        setUsers([]);
        setWarning((prev) => prev || 'No se pudo cargar el listado de cajeros.');
      }
    } catch {
      setError('No se pudieron cargar los datos del resumen de payroll.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [storeId, filters.from, filters.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const dailyShiftRows = useMemo(() => {
    const grouped = new Map();

    asArray(historyRows)
      .filter((row) => !!row.close_at)
      .filter((row) => isDateWithinRange(row.open_at, filters.from, filters.to))
      .forEach((row) => {
        const userId = resolveGroupingId(row);
        if (filters.user_id && userId !== filters.user_id) return;

        const openAt = new Date(row.open_at);
        const closeAt = new Date(row.close_at);
        if (Number.isNaN(openAt.getTime()) || Number.isNaN(closeAt.getTime()) || closeAt <= openAt) return;

        let cursor = openAt;
        while (cursor < closeAt) {
          const { dayStart, shiftChange, dayEnd, wallDate } = getArgentinaDayBoundaries(cursor);

          const segmentEnd = minDate(closeAt, dayEnd);

          let morningHours = 0;
          let afternoonHours = 0;

          if (cursor < shiftChange) {
            const morningEnd = minDate(segmentEnd, shiftChange);
            if (morningEnd > cursor) {
              morningHours += (morningEnd.getTime() - cursor.getTime()) / HOUR_MS;
            }
          }

          if (segmentEnd > shiftChange) {
            const afternoonStart = maxDate(cursor, shiftChange);
            if (segmentEnd > afternoonStart) {
              afternoonHours += (segmentEnd.getTime() - afternoonStart.getTime()) / HOUR_MS;
            }
          }

          const dateKey = toDateKey(dayStart);
          const key = `${dateKey}__${userId}`;

          if (!grouped.has(key)) {
            grouped.set(key, {
              dateKey,
              dateLabel: wallDate.toLocaleDateString('es-AR', {
                timeZone: 'UTC',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }),
              userId,
              cashierName: resolveGroupingLabel(row, usersMap),
              morningHours: 0,
              afternoonHours: 0,
              open_at: null,
              close_at: null,
            });
          }

          const current = grouped.get(key);
          current.morningHours += morningHours;
          current.afternoonHours += afternoonHours;
          // Update to earliest open and latest close
          if (!current.open_at || openAt < current.open_at) current.open_at = new Date(openAt);
          if (!current.close_at || closeAt > current.close_at) current.close_at = new Date(closeAt);

          cursor = segmentEnd;
        }
      });

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        morningHours: round2(row.morningHours),
        afternoonHours: round2(row.afternoonHours),
        totalHours: round2(row.morningHours + row.afternoonHours),
      }))
      .sort((a, b) => {
        if (a.dateKey === b.dateKey) return a.cashierName.localeCompare(b.cashierName);
        return a.dateKey < b.dateKey ? 1 : -1;
      });
  }, [historyRows, filters.from, filters.to, filters.user_id, usersMap]);

  const weeklyShiftRows = useMemo(() => {
    const grouped = new Map();

    dailyShiftRows.forEach((row) => {
      const dateObj = parseDateKey(row.dateKey);
      if (!dateObj) return;

      const weekStart = getWeekStart(dateObj);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const weekKey = toDateKey(weekStart);
      const key = `${weekKey}__${row.userId}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          weekKey,
          weekLabel: `${formatDateLabel(weekStart)} - ${formatDateLabel(weekEnd)}`,
          userId: row.userId,
          cashierName: row.cashierName,
          morningHours: 0,
          afternoonHours: 0,
          totalHours: 0,
          open_at: null,
          close_at: null,
        });
      }

      const current = grouped.get(key);
      current.morningHours += row.morningHours;
      current.afternoonHours += row.afternoonHours;
      current.totalHours += row.totalHours;
      // Keep earliest open and latest close across the week
      if (!current.open_at || row.open_at < current.open_at) current.open_at = row.open_at;
      if (!current.close_at || row.close_at > current.close_at) current.close_at = row.close_at;
    });

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        morningHours: round2(row.morningHours),
        afternoonHours: round2(row.afternoonHours),
        totalHours: round2(row.totalHours),
      }))
      .sort((a, b) => {
        if (a.weekKey === b.weekKey) return a.cashierName.localeCompare(b.cashierName);
        return a.weekKey < b.weekKey ? 1 : -1;
      });
  }, [dailyShiftRows]);

  const activeTableRows = shiftView === 'weekly' ? weeklyShiftRows : dailyShiftRows;

  const activeRowsWithSelection = useMemo(() => {
    const rate = Number(hourlyRate || 0);
    return activeTableRows.map((row) => {
      const selectedHours = getSelectedHoursFromRow(row, normalizedSelectedShifts);
      return {
        ...row,
        selectedHours,
        selectedPrice: round2(selectedHours * rate),
      };
    });
  }, [activeTableRows, normalizedSelectedShifts, hourlyRate]);

  const kpis = useMemo(() => {
    const totalHours = activeRowsWithSelection.reduce((acc, row) => acc + Number(row.selectedHours || 0), 0);
    const totalBase = activeRowsWithSelection.reduce((acc, row) => acc + Number(row.selectedPrice || 0), 0);
    const totalBonus = asArray(adjustmentsRows)
      .filter((item) => isDateWithinRange(item.effective_date, filters.from, filters.to))
      .reduce((acc, item) => acc + (item.type === 'BONUS' ? Number(item.amount || 0) : 0), 0);
    const totalDiscount = asArray(adjustmentsRows)
      .filter((item) => isDateWithinRange(item.effective_date, filters.from, filters.to))
      .reduce((acc, item) => acc + (item.type === 'DISCOUNT' ? Number(item.amount || 0) : 0), 0);
    const totalEstimated = round2(totalBase + totalBonus - totalDiscount);

    return {
      totalHours: round2(totalHours),
      totalBase: round2(totalBase),
      totalBonus: round2(totalBonus),
      totalDiscount: round2(totalDiscount),
      totalEstimated,
    };
  }, [activeRowsWithSelection, adjustmentsRows, filters.from, filters.to]);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 2,
          borderRadius: 3,
          color: '#0f172a',
          border: '1px solid #dbeafe',
          background: 'linear-gradient(135deg, #e0f2fe 0%, #f8fafc 45%, #fef9c3 100%)',
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="h6" fontWeight={800}>Payroll por Turnos</Typography>
          <Typography variant="body2" color="text.secondary">
            Seleccioná qué turnos querés sumar y visualizá horas/precio por día o por semana.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`Vista: ${shiftView === 'weekly' ? 'Semanal' : 'Diaria'}`} color="primary" variant="outlined" />
            <Chip label={`Turnos: ${formatSelectedShiftLabel(normalizedSelectedShifts)}`} color="warning" variant="outlined" />
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={2}>
        <TextField
          label="Desde"
          type="date"
          InputLabelProps={{ shrink: true }}
          size="small"
          value={filters.from}
          onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
        />
        <TextField
          label="Hasta"
          type="date"
          InputLabelProps={{ shrink: true }}
          size="small"
          value={filters.to}
          onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
        />
        <TextField
          label="Cajero"
          size="small"
          select
          value={filters.user_id}
          onChange={(e) => setFilters((prev) => ({ ...prev, user_id: e.target.value }))}
          sx={{ minWidth: 210 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {asArray(users).map((u) => {
            const userId = u._id || u.id;
            return (
              <MenuItem key={userId} value={userId}>
                {resolveCashierName(userId, usersMap)}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          label="Valor hora"
          type="number"
          size="small"
          inputProps={{ min: 0, step: 0.01 }}
          value={hourlyRate}
          onChange={(e) => setHourlyRate(Number(e.target.value || 0))}
          sx={{ minWidth: 180 }}
        />
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 1.25, md: 1.5 }, mb: 2, borderColor: '#e2e8f0' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Turnos a sumar</Typography>
            <Typography variant="caption" color="text.secondary">Podés elegir uno o ambos turnos.</Typography>
          </Box>
          <ToggleButtonGroup
            value={normalizedSelectedShifts}
            onChange={(_, next) => {
              if (!next || next.length === 0) return;
              setSelectedShifts(next);
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            <ToggleButton value="morning" sx={{ textTransform: 'none', fontWeight: 700 }}>Mañana</ToggleButton>
            <ToggleButton value="afternoon" sx={{ textTransform: 'none', fontWeight: 700 }}>Tarde</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={loadData}>Reintentar</Button>}>
          {error}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}

      <Grid container spacing={2} mb={2}>
        <KpiCard
          icon={<Schedule color="primary" />}
          title="Horas seleccionadas"
          value={formatHours(kpis.totalHours)}
          subtitle={formatSelectedShiftLabel(normalizedSelectedShifts)}
        />
        <KpiCard
          icon={<MonetizationOn color="primary" />}
          title="Base por horas"
          value={formatARS(kpis.totalBase)}
        />
        <KpiCard
          icon={<MonetizationOn color="success" />}
          title="Total bonos"
          value={formatARS(kpis.totalBonus)}
        />
        <KpiCard
          icon={<RemoveCircleOutline color="warning" />}
          title="Total descuentos"
          value={formatARS(kpis.totalDiscount)}
        />
        <KpiCard
          icon={<Assessment color="secondary" />}
          title="Total estimado a pagar"
          value={formatARS(kpis.totalEstimated)}
        />
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
        <Box px={2} py={1.5} sx={{ background: 'linear-gradient(120deg,#f8fafc,#eef2ff)', borderBottom: '1px solid #e5e7eb' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {shiftView === 'weekly' ? 'Horas por semana (Mañana / Tarde)' : 'Horas por día (Mañana / Tarde)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mañana: 00:00-15:59 · Tarde: 16:00-23:59
          </Typography>
            </Box>
            <Tabs
              value={shiftView}
              onChange={(_, value) => setShiftView(value)}
              sx={{ minHeight: 34, '& .MuiTab-root': { minHeight: 34, py: 0.3, textTransform: 'none' } }}
            >
              <Tab value="daily" label="Diario" />
              <Tab value="weekly" label="Semanal" />
            </Tabs>
          </Box>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{shiftView === 'weekly' ? 'Semana' : 'Fecha'}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Referencia</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Horas mañana</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Horas tarde</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Horas seleccionadas</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Precio seleccionado</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Detalle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 6 }).map((_, idx) => (
              <TableRow key={`shift-sk-${idx}`}>
                <TableCell><Skeleton width={110} /></TableCell>
                <TableCell><Skeleton width={160} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={120} /></TableCell>
                <TableCell align="right"><Skeleton width={60} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            ))}

            {!loading && activeRowsWithSelection.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay horas para mostrar en el período</Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && activeRowsWithSelection.map((row) => (
              <TableRow key={shiftView === 'weekly' ? `${row.weekKey}-${row.userId}` : `${row.dateKey}-${row.userId}`} hover>
                <TableCell>{shiftView === 'weekly' ? row.weekLabel : row.dateLabel}</TableCell>
                <TableCell>{row.cashierName}</TableCell>
                <TableCell>{formatHours(row.morningHours)}</TableCell>
                <TableCell>{formatHours(row.afternoonHours)}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{formatHours(row.selectedHours)}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{formatARS(row.selectedPrice)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setSelectedShiftRow(row)} title="Ver detalle del precio">
                    <Visibility fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Paper>

      <Dialog open={!!selectedShiftRow} onClose={() => setSelectedShiftRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>Detalle del precio por turno</DialogTitle>
        <DialogContent dividers>
          {selectedShiftRow && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>{selectedShiftRow.cashierName}</Typography>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">{shiftView === 'weekly' ? 'Semana' : 'Fecha'}</Typography>
                <Typography fontWeight={600}>{shiftView === 'weekly' ? selectedShiftRow.weekLabel : selectedShiftRow.dateLabel}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Apertura de caja</Typography>
                <Typography fontWeight={600}>{formatTime(selectedShiftRow.open_at)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Cierre de caja</Typography>
                <Typography fontWeight={600}>{formatTime(selectedShiftRow.close_at)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Tarifa por hora</Typography>
                <Typography fontWeight={600}>{formatARS(hourlyRate)}</Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Horario Mañana</Typography>
                <Typography fontWeight={600}>00:00 - 15:59</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Horas mañana</Typography>
                <Typography fontWeight={600}>{formatHours(selectedShiftRow.morningHours)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Precio mañana</Typography>
                <Typography fontWeight={600}>{formatARS(round2(selectedShiftRow.morningHours * Number(hourlyRate || 0)))}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Horario Tarde</Typography>
                <Typography fontWeight={600}>16:00 - 23:59</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Horas tarde</Typography>
                <Typography fontWeight={600}>{formatHours(selectedShiftRow.afternoonHours)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Precio tarde</Typography>
                <Typography fontWeight={600}>{formatARS(round2(selectedShiftRow.afternoonHours * Number(hourlyRate || 0)))}</Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Turnos seleccionados</Typography>
                <Typography fontWeight={600}>{formatSelectedShiftLabel(normalizedSelectedShifts)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography color="text.secondary">Horas seleccionadas</Typography>
                <Typography fontWeight={600}>{formatHours(selectedShiftRow.selectedHours)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2} pt={1} borderTop="1px solid rgba(0,0,0,0.08)">
                <Typography fontWeight={700}>Precio total seleccionado</Typography>
                <Typography fontWeight={700}>{formatARS(selectedShiftRow.selectedPrice)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedShiftRow(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PayrollSummaryPage;
